import React, { Component, Fragment } from 'react'
import { inject, observer } from 'mobx-react'
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl'
import { withRouter } from 'react-router'
import cx from 'classnames'
import {
  BookAndTrophyIcon,
  Button,
  CertificateIcon,
  Heading,
  Link,
  Loader,
} from '@/components'
import './ExamBanner.scss'

const ExamBannerMessages = defineMessages({
  bodyPassed: {
    defaultMessage: 'You can download your proof of certification below.',
    description: 'Exam Banner Body - Passed state',
    id: 'ExamBanner.bodyPassed',
  },
  bodyPassedNoCertificate: {
    defaultMessage:
      'Good job! You can go back into any Section and review the Course content!',
    description: 'Exam Banner Body - Passed state w/ no Certificate',
    id: 'ExamBanner.bodyPassedNoCertificate',
  },
  bodyPassedCPE: {
    defaultMessage:
      'You are eligible to receive {nasbaCpeCredits} CPE credit for this course. You may also download your certification below for your own records.',
    description: 'Exam Banner Body - Passed state with CPE credits call out',
    id: 'ExamBanner.bodyPassedCPE',
  },
  bodyPassedCPENoCertificate: {
    defaultMessage:
      'You are eligible to receive {nasbaCpeCredits} CPE credit for this course.',
    description:
      'Exam Banner Body - Passed state with CPE credits call out and no certificate',
    id: 'ExamBanner.bodyPassedCPENoCertificate',
  },
  bodyPassedSurveyCta: {
    defaultMessage: `Please don't forget to take the Course Survey, if you have not already.`,
    description: 'Exam Banner Body - Survey CTA after passing Exam',
    id: 'ExamBanner.bodyPassedSurveyCta',
  },
  bodyQuiz: {
    defaultMessage:
      'But first, you must master Quiz for the Sections under Quiz Mastery in the Dashboard below!',
    description: 'Exam Banner Body - Quiz required state',
    id: 'ExamBanner.bodyQuiz',
  },
  bodyExamOnlyReady: {
    defaultMessage:
      'You will need to score a minimum of {scoreRequired}% or higher on at least {examPassCount} attempts.',
    description:
      'Exam Banner Heading - Ready/Started state w/ Exam Only set to true',
    id: 'ExamBanner.bodyExamOnlyReady',
  },
  bodyExamOnlyFailed: {
    defaultMessage:
      'In order to pass this Exam, you will need to score a minimum of {scoreRequired}% or higher, on at least {examPassCount} attempts, before you complete.',
    description:
      'Exam Banner Heading - Finished state w/ failing score & Exam Only set to true',
    id: 'ExamBanner.bodyExamOnlyFailed',
  },
  bodyExamOnlyPassed: {
    defaultMessage: `You've passed your latest Exam attempt! You will need to score a minimum of {scoreRequired}% or higher, on {examPassCountRemaining} more attempts, before you complete.`,
    description:
      'Exam Banner Heading - Finished state w/ passing score & Exam Only set to true',
    id: 'ExamBanner.bodyExamOnlyPassed',
  },
  bodyExamUnlockReady: {
    defaultMessage:
      'To master Exam, you will need to score a minimum of {scoreRequired}% or higher on at least {examPassCount} attempts.',
    description: 'Exam Banner Heading (Exam Unlocked) - Ready/Started state',
    id: 'ExamBanner.bodyExamUnlockReady',
  },
  bodyExamUnlockPassed: {
    defaultMessage: `You've passed your latest Exam attempt! You will need to score a minimum of {scoreRequired}% or higher, on {examPassCountRemaining} more attempts, before you master Exam.`,
    description:
      'Exam Banner Heading (Exam Unlocked) - Finished state w/ passing score',
    id: 'ExamBanner.bodyExamUnlockPassed',
  },
  bodyExamUnlockDone: {
    defaultMessage: `Continue to practice the FAA Written & ChatDME\u2122 Oral Exams, or complete and review the Course content!`,
    description: 'Exam Banner Heading (Exam Unlocked) - Exam Passed state',
    id: 'ExamBanner.bodyExamUnlockDone',
  },
  bodyReady: {
    defaultMessage:
      'You will need to take the Final Exam and score a minimum of {scoreRequired}% or higher.',
    description: 'Exam Banner Body - Ready state',
    id: 'ExamBanner.bodyReady',
  },
  bodyReadyCPE: {
    defaultMessage:
      'In order to receive CPE credits for the course, you will need to take the Final Exam and score a minimum of {scoreRequired}% or higher.',
    description: 'Exam Banner Body - Ready state with CPE credits call out',
    id: 'ExamBanner.bodyReadyCPE',
  },
  bodyReattempt: {
    defaultMessage: `You have not passed your Final Exam. You will need to retake your Final Exam and score a minimum of {scoreRequired}% or higher for a passing score.`,
    description: 'Exam Banner Body - Reattempt state',
    id: 'ExamBanner.bodyReattempt',
  },
  bodyReattemptCPE: {
    defaultMessage: `You have not passed your Final Exam. In order to receive CPE credits for this course, you will need to retake your Final Exam and score a minimum of {scoreRequired}% or higher.`,
    description: 'Exam Banner Body - Reattempt state with CPE credits call out',
    id: 'ExamBanner.bodyReattemptCPE',
  },
  bodySurveyCta: {
    defaultMessage: `When finished, please take the Course Survey to leave your feedback.`,
    description: 'Exam Banner Body - Survey CTA',
    id: 'ExamBanner.bodySurveyCta',
  },
  buttonBeginExam: {
    defaultMessage: 'Take Final Exam',
    description: 'Exam Banner Button — Begin Exam',
    id: 'ExamBanner.buttonBeginExam',
  },
  buttonContinueExam: {
    defaultMessage: 'Continue Final Exam',
    description: 'Exam Banner Button — Continue Exam',
    id: 'ExamBanner.buttonContinueExam',
  },
  buttonDownloadCertificate: {
    defaultMessage: 'Download Certificate',
    description: 'Exam Banner Button — Continue Exam',
    id: 'ExamBanner.buttonDownloadCertificate',
  },
  buttonReattemptExam: {
    defaultMessage: 'Retake Final Exam',
    description: 'Exam Banner Button — Reattempt Exam',
    id: 'ExamBanner.buttonReattemptExam',
  },
  buttonSurvey: {
    defaultMessage: 'Take Course Survey',
    description: 'Exam Banner Button — Take survey',
    id: 'ExamBanner.buttonSurvey',
  },
  examPageLink: {
    defaultMessage: 'Go to Exam',
    description:
      'Exam Banner Button — Exam Page link when Practice Mode is enabled',
    id: 'ExamBanner.examPageLink',
  },
  headingExamOnlyReady: {
    defaultMessage: 'Now, it is time to take your {courseTitle}',
    description:
      'Exam Banner Heading - Ready/Started state w/ Exam Only set to true',
    id: 'ExamBanner.headingExamOnlyReady',
  },
  headingExamOnlyFailed: {
    defaultMessage: 'You have not passed your {courseTitle}',
    description:
      'Exam Banner Heading - Finished state w/ failing score & Exam Only set to true',
    id: 'ExamBanner.headingExamOnlyFailed',
  },
  headingExamOnlyPassed: {
    defaultMessage: 'You have passed your {courseTitle}',
    description:
      'Exam Banner Heading - Finished state w/ passing score, Exam requirements met, and Exam Only set to true',
    id: 'ExamBanner.headingExamOnlyPassed',
  },
  headingExamUnlockReady: {
    defaultMessage:
      'Challenge your mastery by taking the FAA Written and ChatDME\u2122 Oral Practice Exams',
    description: 'Exam Banner Heading (Exam Unlocked) - Ready/Started state',
    id: 'ExamBanner.headingExamUnlockReady',
  },
  headingExamUnlockDone: {
    defaultMessage:
      'Congratulations, you have mastered the FAA Written Exam for the Course!',
    description: 'Exam Banner Heading (Exam Unlocked) - Passed state',
    id: 'ExamBanner.headingExamUnlockDone',
  },
  headingReady: {
    defaultMessage:
      'Now it is time to take and pass the Final Exam to complete the course.',
    description: 'Exam Banner Heading - Ready/Started state',
    id: 'ExamBanner.headingReady',
  },
  headingReattempt: {
    defaultMessage: 'Retake and pass the Final Exam to complete the course.',
    description: 'Exam Banner Heading - Reattempt state',
    id: 'ExamBanner.headingReattempt',
  },
  headingPassed: {
    defaultMessage:
      'Congratulations, you completed the course and you passed your Final Exam!',
    description: 'Exam Banner Heading - Passed state',
    id: 'ExamBanner.headingPassed',
  },
  headingPassedAttempt: {
    defaultMessage: 'Congratulations, you have passed the Exam attempt!',
    description:
      'Exam Banner Heading - Attempt Passed, Exam Requirements Not Met State',
    id: 'ExamBanner.headingPassedAttempt',
  },
  headingQuiz: {
    defaultMessage:
      "It's almost time to take the Final Exam to complete the course.",
    description: 'Exam Banner Heading - Quiz required state',
    id: 'ExamBanner.headingQuiz',
  },
  labelCompleted: {
    defaultMessage: 'Completed!',
    description: 'Exam Banner Label 1',
    id: 'ExamBanner.labelCompleted',
  },
  labelCourse: {
    defaultMessage: 'Course: {courseTitle}!',
    description: 'Exam Banner Label 2',
    id: 'ExamBanner.labelCourse',
  },
})

function __bodyPassed(examCertificate) {
  if (!examCertificate) return ExamBannerMessages.bodyPassedNoCertificate
  return ExamBannerMessages.bodyPassed
}

function __bodyPassedCpe(examCertificate) {
  if (!examCertificate) {
    return ExamBannerMessages.bodyPassedCPENoCertificate
  }
  return ExamBannerMessages.bodyPassedCPE
}

@injectIntl
@withRouter
@inject('contentStore')
@observer
class ExamBanner extends Component {
  componentDidMount() {
    const { contentStore } = this.props
    contentStore.loadExamStatus()
  }
  onDownloadCertificate = () => {
    const { contentStore } = this.props
    const { exam } = contentStore
    exam.downloadCertificate()
  }
  onOpenExam = () => {
    const { history } = this.props
    history.push('/exam')
  }
  render() {
    const {
      courseTitle,
      contentStore,
      intl,
      isQuiz,
      isQuizMastered,
      isQuizMastery,
    } = this.props
    const { formatMessage } = intl
    const { exam, isLoadingExamStatus, settings } = contentStore
    const {
      examPassCount,
      isDone: isDoneExam,
      isDownloadingCertificate,
      isPassed: isPassedExamAttempt,
      isPassedExam,
      isStarted: isStartedExam,
      nasbaCpeCredits,
      numExamPassed,
      scoreRequired,
      surveyUrl: examSurveyUrl,
    } = exam
    const {
      examCertificate,
      examEndless,
      examOnly,
      examPracticeMode,
      examUnlock,
    } = settings || {}
    let bodyText
    let buttonText
    let headingText
    let surveyText
    let examPassCountRemaining = examPassCount - numExamPassed
    if (isQuiz && isQuizMastery && !isQuizMastered) {
      bodyText = formatMessage(ExamBannerMessages.bodyQuiz)
      headingText = formatMessage(ExamBannerMessages.headingQuiz)
    } else if (isStartedExam) {
      // started
      // - by default, implies not all exam requirements have been met yet
      //   because cannot attempt if requirements have been passed
      // - if exam endless is set to true, this should show the done text
      bodyText = examOnly
        ? formatMessage(ExamBannerMessages.bodyExamOnlyReady, {
            examPassCount,
            scoreRequired,
          })
        : formatMessage(
            nasbaCpeCredits
              ? ExamBannerMessages.bodyReadyCPE
              : ExamBannerMessages.bodyReady,
            { scoreRequired },
          )
      buttonText = formatMessage(ExamBannerMessages.buttonContinueExam)
      headingText = examOnly
        ? formatMessage(ExamBannerMessages.headingExamOnlyReady, {
            courseTitle,
          })
        : formatMessage(ExamBannerMessages.headingReady)
      if (examUnlock) {
        headingText = formatMessage(ExamBannerMessages.headingExamUnlockReady)
        bodyText = null
      }
      if (isPassedExam) {
        bodyText = nasbaCpeCredits
          ? formatMessage(__bodyPassedCpe(examCertificate), {
              nasbaCpeCredits,
            })
          : formatMessage(__bodyPassed(examCertificate))
      }
      surveyText = formatMessage(ExamBannerMessages.bodySurveyCta)
    } else if (isDoneExam && isPassedExam) {
      // exam is complete
      // - passed all exam requirements
      // - cannot continue taking exam
      // - certificate downloadable
      bodyText = nasbaCpeCredits
        ? formatMessage(__bodyPassedCpe(examCertificate), {
            nasbaCpeCredits,
          })
        : formatMessage(__bodyPassed(examCertificate))
      buttonText = formatMessage(ExamBannerMessages.buttonReattemptExam)
      headingText = examOnly
        ? formatMessage(ExamBannerMessages.headingExamOnlyPassed, {
            courseTitle,
          })
        : formatMessage(ExamBannerMessages.headingPassed)
      if (examUnlock) {
        headingText = formatMessage(ExamBannerMessages.headingExamUnlockDone)
        bodyText = formatMessage(ExamBannerMessages.bodyExamUnlockDone)
      }
      surveyText = formatMessage(ExamBannerMessages.bodyPassedSurveyCta)
    } else if (isDoneExam && !isPassedExam && isPassedExamAttempt) {
      // passed attempt, exam unfinished
      // - not all exam requirements have been met yet
      // - can continue taking exam
      // - last attempt was passed
      bodyText = examOnly
        ? formatMessage(ExamBannerMessages.bodyExamOnlyPassed, {
            examPassCountRemaining,
            scoreRequired,
          })
        : formatMessage(
            nasbaCpeCredits
              ? ExamBannerMessages.bodyReattemptCPE
              : ExamBannerMessages.bodyReattempt,
            { scoreRequired },
          )
      buttonText = formatMessage(ExamBannerMessages.buttonReattemptExam)
      headingText = examOnly
        ? formatMessage(ExamBannerMessages.headingExamOnlyReady, {
            courseTitle,
          })
        : formatMessage(ExamBannerMessages.headingReattempt)
      if (examUnlock) {
        headingText = formatMessage(ExamBannerMessages.headingExamUnlockReady)
        bodyText = null
      }
      surveyText = formatMessage(ExamBannerMessages.bodySurveyCta)
    } else if (isDoneExam && !isPassedExam && !isPassedExamAttempt) {
      // failed attempt, exam unfinished
      // - not all exam requirements have been met yet
      // - can continue taking exam
      // - last attempt was failed
      bodyText = examOnly
        ? formatMessage(ExamBannerMessages.bodyExamOnlyFailed, {
            examPassCount,
            scoreRequired,
          })
        : formatMessage(
            nasbaCpeCredits
              ? ExamBannerMessages.bodyReattemptCPE
              : ExamBannerMessages.bodyReattempt,
            { scoreRequired },
          )
      buttonText = formatMessage(ExamBannerMessages.buttonReattemptExam)
      headingText = examOnly
        ? formatMessage(ExamBannerMessages.headingExamOnlyFailed, {
            courseTitle,
          })
        : formatMessage(ExamBannerMessages.headingReattempt)
      if (examUnlock) {
        headingText = formatMessage(ExamBannerMessages.headingExamUnlockReady)
        bodyText = null
      }
      surveyText = formatMessage(ExamBannerMessages.bodySurveyCta)
    } else {
      // ready
      bodyText = examOnly
        ? formatMessage(ExamBannerMessages.bodyExamOnlyReady, {
            examPassCount,
            scoreRequired,
          })
        : formatMessage(
            nasbaCpeCredits
              ? ExamBannerMessages.bodyReadyCPE
              : ExamBannerMessages.bodyReady,
            { scoreRequired },
          )
      buttonText = formatMessage(ExamBannerMessages.buttonBeginExam)
      headingText = examOnly
        ? formatMessage(ExamBannerMessages.headingExamOnlyReady, {
            courseTitle,
          })
        : formatMessage(ExamBannerMessages.headingReady)
      if (examUnlock) {
        headingText = formatMessage(ExamBannerMessages.headingExamUnlockReady)
        bodyText = null
      }
      surveyText = formatMessage(ExamBannerMessages.bodySurveyCta)
    }
    return (
      <div className={cx('ExamBanner', { 'is-loading': isLoadingExamStatus })}>
        {isLoadingExamStatus ? (
          <Loader />
        ) : (
          <Fragment>
            {isPassedExam ? <BookAndTrophyIcon /> : <CertificateIcon />}
            <div className="ExamBanner__content">
              {!examOnly && !examUnlock && (
                <div className="ExamBanner__label-wrapper">
                  <span className="ExamBanner__label ExamBanner__label-check">
                    <i className="ExamBanner__label-check-icon fa fa-check" />
                    {formatMessage(ExamBannerMessages.labelCompleted)}
                  </span>
                  <span className="ExamBanner__label">
                    <FormattedMessage
                      {...ExamBannerMessages.labelCourse}
                      values={{
                        courseTitle: courseTitle,
                      }}
                    />
                  </span>
                </div>
              )}
              <Heading is2>{headingText}</Heading>
              <div className="ExamBanner__description">
                {bodyText && <p>{bodyText}</p>}
                {examSurveyUrl && surveyText && <p>{surveyText}</p>}
              </div>
              {examPassCount > 1 && (
                <div className="ExamBanner__pass-count">
                  Passed:{' '}
                  {numExamPassed > examPassCount
                    ? examPassCount
                    : numExamPassed}{' '}
                  of {examPassCount}
                </div>
              )}
              {(!isQuiz || !isQuizMastery || isQuizMastered) && (
                <div className="ExamBanner__buttons">
                  {examPracticeMode && (
                    <Link isButton isSuccess nav to="/exam">
                      {formatMessage(ExamBannerMessages.examPageLink)}
                    </Link>
                  )}
                  {!examPracticeMode && (!isPassedExam || examEndless) ? (
                    <Button
                      isSuccess
                      onClick={this.onOpenExam}
                      text={buttonText}
                    />
                  ) : null}
                  {examSurveyUrl ? (
                    <Link href={examSurveyUrl} isButton isInfo target="_blank">
                      {formatMessage(ExamBannerMessages.buttonSurvey)}
                    </Link>
                  ) : null}
                  {examCertificate && isPassedExam ? (
                    <Button
                      className="ExamBanner__certificate-button"
                      isLoading={isDownloadingCertificate}
                      isSuccess
                      onClick={this.onDownloadCertificate}
                      text={formatMessage(
                        ExamBannerMessages.buttonDownloadCertificate,
                      )}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </Fragment>
        )}
      </div>
    )
  }
}

export default ExamBanner
